import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/site/Reveal";

const inputCls = "mt-2 w-full border-b border-[#3b1d63] bg-transparent py-3 text-[#f3eefc] outline-none transition-colors placeholder:text-[#7d6a9c] focus:border-[#d8b4fe]";

const Shell = ({ title, sub, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden overflow-hidden lg:block">
      <img src="/img/brand.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#07030c]" />
    </div>
    <div className="relative flex items-center px-5 pb-20 pt-32 md:px-16">
      <div className="pointer-events-none absolute inset-0 overflow-hidden lg:hidden"><img src="/img/brand.jpg" alt="" className="h-full w-full object-cover opacity-15" /></div>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }} className="relative w-full max-w-md">
        <h1 className="font-display text-5xl text-[#f3eefc] md:text-6xl">{title}</h1>
        <p className="mt-4 text-[#cbbbe6]">{sub}</p>
        {children}
      </motion.div>
    </div>
  </div>
);

const useAfter = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  return (u) => nav(sp.get("next") || (u?.role === "admin" ? "/admin" : "/account"));
};

export const Login = () => {
  const { login } = useAuth();
  const after = useAfter();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { after(await login(f.email, f.password)); } catch (x) { setErr(fmtErr(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Sign in to claim pieces and track your requests.">
      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
        <input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="login-email-input" />
        <input type="password" required placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="login-password-input" />
        {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
        <button disabled={busy} className="w-full rounded-full bg-[#7c3aed] py-4 text-sm font-medium text-white transition-colors hover:bg-[#a855f7] disabled:opacity-60" data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</button>
        <p className="text-sm text-[#cbbbe6]">New here? <Link to={`/register${sp.get("next") ? `?next=${encodeURIComponent(sp.get("next"))}` : ""}`} className="text-[#d8b4fe] underline underline-offset-4" data-testid="login-register-link">Create an account</Link></p>
      </form>
    </Shell>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const after = useAfter();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { after(await register(f.name, f.email, f.password)); } catch (x) { setErr(fmtErr(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Join the Maison" sub="Create an account to claim pieces, save favourites and follow your claims.">
      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
        <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="register-name-input" />
        <input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="register-email-input" />
        <input type="password" required minLength={6} placeholder="Password (6+ characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="register-password-input" />
        {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}
        <button disabled={busy} className="w-full rounded-full bg-[#7c3aed] py-4 text-sm font-medium text-white transition-colors hover:bg-[#a855f7] disabled:opacity-60" data-testid="register-submit-button">{busy ? "Creating…" : "Create account"}</button>
        <p className="text-sm text-[#cbbbe6]">Already a member? <Link to="/login" className="text-[#d8b4fe] underline underline-offset-4" data-testid="register-login-link">Sign in</Link></p>
      </form>
    </Shell>
  );
};
